// "In der Sammlung" im Kontaktkopf (ST-SEA-002; ADR-0065 Ziffer 4; CM-SEA-007,
// CM-UX-002, NFR-ACC-001). Vorgabe DESIGN "In der Sammlung im Kontaktkopf".
//
// EINE ZUSATZINFORMATION, KEINE AUFGABE: Der Hinweis steht nur, wenn der Kontakt
// in mindestens einer eigenen offenen Sammlung ist, und erst, wenn ALLE Seiten
// geladen sind -- eine Zahl aus der ersten Seite waere ab 51 falsch. Scheitert
// eine Seite, steht nichts da und kein Satz: der Nutzer hat hier keine Aktion,
// die ein Fehlersatz anleiten koennte (NFR-UX-005 greift nicht).
//
// Im Popover keine Aktion: "Zur Sammlung …" steht im Menue, Entfernen in der
// Sammlung. Die Liste hat keine Grenze und scrollt in sich.
//
// EIN NICHT-MODALER DIALOG OHNE FLUENTS POPOVER: Popover setzt beim ersten
// Oeffnen Fokuswaechter von tabster in den Body, die auch nach dem Schliessen
// stehen bleiben -- mit und ohne trapFocus --, und axe meldet sie als
// aria-hidden-focus (gemessen, A80.10). Die Flaeche steht deshalb im Fluss
// direkt hinter dem Ausloeser, liegt mit Fluents Positionierung `fixed` ueber
// der Karte (die overflow:hidden traegt) und ist nur eingehaengt, solange sie
// offen ist. Tab laeuft durch die Links und weiter im Kopf; verlaesst der Fokus
// die Flaeche oder klickt man daneben, schliesst sie; Esc schliesst und gibt
// den Fokus an den Ausloeser zurueck.

import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { Button, Caption1Strong, makeStyles, tokens, usePositioning } from '@fluentui/react-components';

import { collectionHref, fetchAllPartyCollections, inCollectionsLabel, type CollectionMention } from '../../collections.ts';
import { AppLink } from '../../shell/router.tsx';

/** Haken im 24er-Raster wie die uebrigen Kopfsymbole; stumm, die Zahl steht daneben. */
const HAKEN = (
  <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const useStyles = makeStyles({
  // Wie Fluents MenuPopover "Weitere Aktionen": Flaeche, Radius, schwebender
  // Schatten und ein durchsichtiger Rand, der im Kontrastmodus sichtbar wird.
  flaeche: {
    zIndex: 1000,
    maxWidth: 'min(24rem, calc(100vw - 2rem))',
    padding: '0.75rem 1rem',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    border: `1px solid ${tokens.colorTransparentStroke}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
  },
  liste: {
    listStyle: 'none',
    margin: 0,
    marginBlockStart: '0.5rem',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    maxHeight: 'min(60vh, 20rem)',
    overflowY: 'auto',
    overflowWrap: 'anywhere',
  },
  titel: { margin: 0 },
});

export function InDerSammlung({
  partyId,
  ladung,
  apiBaseUrl,
  accessToken,
}: {
  readonly partyId: string;
  /** Steigt nach jedem Schreiben in eine Sammlung: dann wird neu geladen. */
  readonly ladung: number;
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
}): ReactElement | null {
  const own = useStyles();
  const titelId = useId();
  const dialogId = useId();
  const [stand, setStand] = useState<{ readonly partyId: string; readonly sammlungen: readonly CollectionMention[] } | undefined>(undefined);
  // Nur der juengste Lauf darf schreiben: ein spaeter Lauf eines anderen Kontakts
  // oder einer aelteren Ladung wird verworfen.
  const lauf = useRef(0);
  const [offen, setOffen] = useState(false);
  const ausloeser = useRef<HTMLButtonElement | null>(null);
  // Der Token-Rueckruf ist kein Anlass zum Neuladen, auch wenn er je Darstellung neu ist.
  const token = useRef(accessToken);
  token.current = accessToken;

  useEffect(() => {
    const meiner = ++lauf.current;
    void (async () => {
      try {
        const zugang = await token.current();
        // Schon ueberholt (Kontaktwechsel, neue Ladung, zweiter Effektlauf im
        // StrictMode): dann gar nicht erst abrufen.
        if (meiner !== lauf.current) return;
        if (zugang === undefined) {
          setStand(undefined);
          return;
        }
        const result = await fetchAllPartyCollections({ baseUrl: apiBaseUrl, accessToken: zugang, partyId, gilt: () => meiner === lauf.current });
        if (meiner !== lauf.current) return;
        // Bis zur neuen Zahl bleibt die alte stehen; scheitert das Laden, steht keine.
        setStand(result.outcome === 'loaded' ? { partyId, sammlungen: result.collections } : undefined);
      } catch {
        if (meiner === lauf.current) setStand(undefined);
      }
    })();
  }, [partyId, ladung, apiBaseUrl]);

  const sichtbar = stand !== undefined && stand.partyId === partyId && stand.sammlungen.length > 0;
  // Verschwindet der Hinweis, schliesst auch das Popover.
  useEffect(() => {
    if (!sichtbar) setOffen(false);
  }, [sichtbar]);

  const { targetRef, containerRef } = usePositioning({ position: 'below', align: 'end', strategy: 'fixed', offset: 4 });
  const flaeche = useRef<HTMLDivElement | null>(null);
  // Nach dem Commit, nicht nach einem Zeitgeber: erst dann steht der Link im DOM.
  useEffect(() => {
    if (offen) flaeche.current?.querySelector<HTMLAnchorElement>('a')?.focus();
  }, [offen]);

  // Ein Klick daneben schliesst, ohne den Fokus zu versetzen: er gehoert dem Ziel des Klicks.
  useEffect(() => {
    if (!offen) return undefined;
    const daneben = (ereignis: PointerEvent): void => {
      const ziel = ereignis.target as Node | null;
      if (flaeche.current?.contains(ziel) !== true && ausloeser.current?.contains(ziel) !== true) setOffen(false);
    };
    document.addEventListener('pointerdown', daneben);
    return () => document.removeEventListener('pointerdown', daneben);
  }, [offen]);

  if (!sichtbar) return null;
  return (
    <>
      {/* In der Aktionsreihe des Kopfs wie in v4 ("✓ In der Sammlung"); die Zahl
          bleibt, weil sie mehr sagt als ein Ja. */}
      <Button
        appearance="secondary"
        icon={HAKEN}
        ref={(knoten: HTMLButtonElement | null) => {
          ausloeser.current = knoten;
          targetRef.current = knoten;
        }}
        aria-haspopup="dialog"
        aria-expanded={offen}
        aria-controls={offen ? dialogId : undefined}
        onClick={() => setOffen((war) => !war)}
      >
        {inCollectionsLabel(stand.sammlungen.length)}
      </Button>
      {offen ? (
        <div
          ref={(knoten: HTMLDivElement | null) => {
            flaeche.current = knoten;
            containerRef.current = knoten;
          }}
          id={dialogId}
          role="dialog"
          aria-labelledby={titelId}
          className={own.flaeche}
          onKeyDown={(ereignis) => {
            if (ereignis.key !== 'Escape') return;
            ereignis.stopPropagation();
            setOffen(false);
            ausloeser.current?.focus();
          }}
          onBlur={(ereignis) => {
            const naechstes = ereignis.relatedTarget as Node | null;
            if (naechstes !== null && flaeche.current?.contains(naechstes) !== true && ausloeser.current?.contains(naechstes) !== true) setOffen(false);
          }}
        >
          <Caption1Strong as="h2" id={titelId} className={own.titel}>
            Sammlungen mit diesem Kontakt
          </Caption1Strong>
          <ul className={own.liste}>
            {stand.sammlungen.map((sammlung) => (
              <li key={sammlung.collectionId}>
                <AppLink to={collectionHref(sammlung.collectionId)}>{sammlung.collectionName}</AppLink>
              </li>
            ))}
          </ul>
        </div>
      ) : undefined}
    </>
  );
}
